import { supabase } from '../../../../utils/supabase';
import { generateOptimalTeams, resultToTeamAssignments } from './index';
import { buildTeamAssignmentData } from '../teamAssignmentData';
import type { TeamAssignmentData } from '../types';

export interface GameBalance {
  blueTeam: string[];
  orangeTeam: string[];
  teamAssignments: TeamAssignmentData;
}

/**
 * True when a saved balance covers exactly the given players.
 * A dropout or reserve promotion after the balance was saved makes it stale.
 */
export function savedTeamsMatchRoster(
  savedTeams: Array<{ player_id: string }> | null | undefined,
  playerIds: string[]
): boolean {
  if (!savedTeams || savedTeams.length !== playerIds.length) return false;
  const roster = new Set(playerIds);
  return savedTeams.every((t) => roster.has(t.player_id));
}

/**
 * Run the brute-force optimal balancer for a game (honouring its permanent
 * goalkeepers) and save the result to `balanced_team_assignments` — the same
 * payload the admin page's Save writes, so the pg_cron auto-announce and the
 * admin page both pick it up.
 *
 * Reads position/attribute data through the signed-in user's session; callers
 * must only run this for authenticated users, or the teams are built blind.
 */
export async function balanceGameWithBruteForce(
  gameId: string,
  playerIds: string[]
): Promise<GameBalance> {
  const { data: gkRows, error: gkError } = await supabase
    .from('permanent_goalkeepers')
    .select('player_id')
    .eq('game_id', gameId);

  if (gkError) throw gkError;

  const permanentGKIds = ((gkRows ?? []) as Array<{ player_id: string }>)
    .map((row) => row.player_id)
    .filter((id) => playerIds.includes(id));

  const result = await generateOptimalTeams(playerIds, { permanentGKIds });
  const { blueTeam, orangeTeam } = resultToTeamAssignments(result);
  const teamAssignments = buildTeamAssignmentData([...blueTeam, ...orangeTeam]);

  const { error } = await supabase
    .from('balanced_team_assignments')
    .upsert({ game_id: gameId, team_assignments: teamAssignments });

  if (error) throw error;

  return {
    blueTeam: blueTeam.map((p) => p.player_id),
    orangeTeam: orangeTeam.map((p) => p.player_id),
    teamAssignments,
  };
}
